import { useEffect, useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { Locale } from "@/lib/i18n/config";
import { getSellerPath } from "@/lib/routing/portal-urls";
import { useStoreSubscriptionQuery } from "@/modules/dashboard/services/useStoreSubscriptionQuery";
import { useDashboardAnalyticsQuery } from "@/modules/dashboard/services/useDashboardAnalyticsQuery";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { LoadingSkeleton } from "@a2z/ui";
import { ErrorState } from "@/shared/components/ErrorState";
import { usePageChrome } from "@/shared/layout/PageChromeContext";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { usePullToRefresh } from "@/shared/pull-to-refresh";
import { PageTransition } from "@/shared/components/PageTransition";
import type { SellerDashboardRange } from "@/lib/api/types";
import { Link } from "react-router-dom";
import { DashboardHeadlineTiles } from "@/modules/dashboard/components/redesign/DashboardHeadlineTiles";
import { DashboardRevenueCard } from "@/modules/dashboard/components/redesign/DashboardRevenueCard";
import { DashboardOrderFateCard } from "@/modules/dashboard/components/redesign/DashboardOrderFateCard";
import { DashboardStoreStatsCard } from "@/modules/dashboard/components/redesign/DashboardStoreStatsCard";
import { DashboardPaymentHealthPanel } from "@/modules/dashboard/components/redesign/DashboardPaymentHealthPanel";
import { DashboardDiscountImpactPanel } from "@/modules/dashboard/components/redesign/DashboardDiscountImpactPanel";
import { DashboardLossBreakdownPanel } from "@/modules/dashboard/components/redesign/DashboardLossBreakdownPanel";
import { DashboardTopProductsCard } from "@/modules/dashboard/components/redesign/DashboardTopProductsCard";
import type {
  DashboardFateSegment,
  DashboardGovernorateBar,
  DashboardHeadlineTile,
  DashboardPaymentMixSlice,
  DashboardStoreStat,
} from "@/modules/dashboard/types/dashboard-analytics.types";
import {
  formatCurrency,
  formatCurrencyParts,
} from "@/lib/formatting/currency";
import type { CurrencyParts } from "@/lib/formatting/types/currency.types";
import { isolate } from "@/lib/formatting/bidi";
import { getLocaleDirection } from "@/lib/i18n/config";
import { intlLocale } from "@/lib/formatting/locale";

type DashboardPageProps = {
  locale: string;
  storeSlug: string;
};


/**
 * The two validated categorical chart slots, in fixed order: slot 1 = sales,
 * slot 2 = profit. Colour follows the measure, never its rank, so the series
 * keep their identity across ranges. Both are tokens defined and documented in
 * app/styles.css, where the palette-validator output is recorded.
 */
const CHART_SALES_COLOR = "var(--chart-sales)";
const CHART_PROFIT_COLOR = "var(--chart-profit)";

/** The four windows the selector offers, shortest first. 30d is the default. */
const RANGE_OPTIONS: SellerDashboardRange[] = ["24h", "7d", "30d", "90d"];
const DEFAULT_RANGE: SellerDashboardRange = "30d";

/**
 * Two label sets per window, both localized (Arabic gets ٢٤س / ٧ي / ٣٠ي / ٩٠ي,
 * English keeps 24h / 7d / 30d / 90d):
 *
 * - `pill` is the terse selector label, sized to fit four buttons on a 375px row.
 * - `long` is the prose form ("٣٠ يوم") interpolated into the page title and the
 *   chart subtitle, where "٣٠ي" would read as an abbreviation nobody asked for.
 */
const RANGE_PILL_KEYS: Record<SellerDashboardRange, string> = {
  "24h": keys.dashboard.rangePill24h,
  "7d": keys.dashboard.rangePill7d,
  "30d": keys.dashboard.rangePill30d,
  "90d": keys.dashboard.rangePill90d,
};

const RANGE_LONG_KEYS: Record<SellerDashboardRange, string> = {
  "24h": keys.dashboard.rangeLong24h,
  "7d": keys.dashboard.rangeLong7d,
  "30d": keys.dashboard.rangeLong30d,
  "90d": keys.dashboard.rangeLong90d,
};

/**
 * The API returns one ISO timestamp per bucket (up to 90 of them). Localize
 * EVERY one — clock time for the hourly 24h range, month/day for the daily
 * ranges — because the chart reads them three ways: it samples a few for the
 * x-axis, names the hovered bucket in the tooltip, and lists them all in the
 * table view. Sampling here would leave the tooltip and table unlabelled.
 * Localizing on the client (not server-side) keeps the response
 * locale-agnostic.
 */
function localizeBucketLabels(
  bucketLabels: string[],
  range: SellerDashboardRange,
  locale: string,
): string[] {
  const format = (date: Date): string =>
    range === "24h"
      ? date.toLocaleTimeString(intlLocale(locale), { hour: "numeric", hour12: true })
      : date.toLocaleDateString(intlLocale(locale), { month: "short", day: "numeric" });

  return bucketLabels.map((iso) => {
    const date = new Date(iso);
    // A malformed timestamp must not render "Invalid Date" to the seller.
    return Number.isNaN(date.getTime()) ? "" : format(date);
  });
}

export function DashboardPage({ locale, storeSlug }: DashboardPageProps) {
  const t = useTranslations();
  const isMobile = useIsMobile();
  // Declared before the queries because the analytics request is keyed on it —
  // changing the range refetches (see useDashboardAnalyticsQuery).
  const [range, setRange] = useState<SellerDashboardRange>(DEFAULT_RANGE);
  const { isLoading, error, refetch } = useStoreSubscriptionQuery({ storeSlug });
  const { data: sessionData } = useSessionQuery({ storeSlug });
  const {
    data: analytics,
    isLoading: analyticsLoading,
    error: analyticsError,
    refetch: refetchAnalytics,
  } = useDashboardAnalyticsQuery({ storeSlug, range });

  // Pull-to-refresh (mobile PWA): refresh every source the dashboard renders —
  // subscription, analytics and the session/overview flags. Awaited together so
  // the spinner holds until all settle.
  usePullToRefresh(() => Promise.all([refetch(), refetchAnalytics()]));

  // Every number on this page comes from one of these queries — while any is
  // loading (or has failed) the sections would render as fake zeros, so the
  // page gates on them below instead of showing made-up values. Note the
  // analytics query keeps the previous range's data during a range flip, so
  // `analyticsLoading` is only true on the very first load.
  const statsLoading = analyticsLoading;
  const statsFailed = Boolean(analyticsError);

  const { setHero, setHeroAux } = usePageChrome();

  const seller = sessionData?.seller ?? null;

  // The same "needs attention" bucket the Orders page shows, straight from the
  // API. This used to hand-sum three statuses, which is a second definition of
  // "needs action" that silently drifts from the Orders page whenever the
  // bucket's membership changes server-side. No extra request: the counts query
  // already returns the buckets.
  const productsNewHref = getSellerPath(
    locale,
    storeSlug,
    "/products?create=1",
  );
  const productsListHref = getSellerPath(locale, storeSlug, "/products");
  // Resolve translation strings up-front — `t` itself is a fresh closure on
  // every render (see useTranslations), so depending on it in `useMemo`
  // recomputes the result every render and would cascade into loops.
  const avgOrderValueLabel = t(keys.dashboard.kpiAvgOrderValue);
  const dashboardTitleFallback = t(keys.seller.dashboardTitle);
  const addProductLabel = t(keys.seller.quickActionAddProduct);

  /**
   * The page is titled by WHAT it reports on — this store, over this window —
   * not by a greeting. A greeting states the time of day, which the seller
   * already knows, and pushed the two facts that actually orient the reader
   * (which store, which range) off the screen entirely.
   */
  const storeName = seller?.storeName?.trim() || dashboardTitleFallback;
  const rangeLongLabel = t(RANGE_LONG_KEYS[range]);
  const pageTitle = t(keys.dashboard.headerTitle, {
    store: storeName,
    range: rangeLongLabel,
  });

  // Percent deltas come from the API. `null` means "no baseline" (the previous
  // period was 0), and `KpiInput.delta` uses `undefined` for the same idea —
  // the tile then renders no delta line at all rather than "0%" or "+∞".
  const toDelta = (deltaPct: number | null): number | undefined =>
    deltaPct ?? undefined;

  /**
   * Low-stock membership stays DERIVED ON THE CLIENT, deliberately.
   *
   * `deriveProductStatus` uses the product's own `lowStockThreshold`, falling
   * back to the store default and then 10, and returns "active" for unlimited
   * stock. The backend's `status=low_stock` product filter (see
   * apps/backend/.../product-filters.helper.ts) hardcodes `LOW_STOCK_THRESHOLD
   * = 5` and has no unlimited-stock branch. The two therefore disagree: a
   * product with 8 units is low-stock here and NOT low-stock server-side, and
   * an unlimited-stock variant with stockCount 3 would be reported low-stock by
   * the server but is unlimited (never low) here. Swapping to the server filter
   * to save a fetch would silently change which products the seller sees, so we
   * keep the client derivation and keep fetching the catalog. Flagged for the
   * backend owner rather than papered over.
   *
   * Sorted most-depleted first. Products at 0 purchasable stock are
   * out-of-stock, a separate concern, and `deriveProductStatus` excludes them.
   */
  // The redesigned dashboard renders its own greeting + stat cards inline in
  // the scroll body (like Coupons / Reviews), so clear the shared hero chrome.
  useEffect(() => {
    setHero(null);
    setHeroAux(null);
    return () => {
      setHero(null);
      setHeroAux(null);
    };
  }, [setHero, setHeroAux]);

  if (isLoading || statsLoading) {
    return <LoadingSkeleton variant="card" rows={3} />;
  }
  if (error) {
    return <ErrorState onRetry={() => void refetch()} />;
  }
  if (statsFailed || !analytics) {
    // The analytics query failed — every section on this page derives from it,
    // so rendering would show fake zero KPIs. `!analytics` lands here too: no
    // aggregates means nothing honest to draw.
    return <ErrorState onRetry={() => void refetchAnalytics()} />;
  }

  const productEditHref = (productId: string): string =>
    `${productsListHref}/${productId}`;

  // `ordersMissingCost > 0` means some sold items carry no recorded cost, so
  // their full price counted as profit — the Profit tile is an upper bound and
  // must say so rather than quietly overstating.
  const profitHint =
    analytics.ordersMissingCost > 0
      ? t(keys.dashboard.kpiProfitMissingCost, {
          count: analytics.ordersMissingCost,
        })
      : undefined;

  const money = (value: number): string => formatCurrency(value, locale, "EGP");
  /**
   * The same money, split into figure + unit, for the places where the number
   * is the HEADLINE and the symbol has to be typeset as a subordinate unit.
   * `money` stays for body text, bar summaries and tooltips, where the value
   * sits inside a sentence and must read as one joined run.
   */
  const moneyParts = (value: number): CurrencyParts =>
    formatCurrencyParts(value, locale, "EGP");
  const percent = (fraction: number): string =>
    new Intl.NumberFormat(intlLocale(locale), {
      style: "percent",
      maximumFractionDigits: 1,
    }).format(fraction);
  /** Plural-aware "N orders" — the count that must accompany every money figure. */
  /** A bare integer in the locale's numerals — for counts that are not plurals. */
  const countValue = (value: number): string =>
    value.toLocaleString(intlLocale(locale));

  const orderCount = (count: number): string =>
    t(keys.dashboard.ordersCount, { count });
  /**
   * The readout every bar carries in BOTH channels — `title` for the mouse and
   * `aria-label` for keyboard/AT. Defined once here (the page is where `t`
   * lives) and handed to each card, so the dash/middot punctuation stays a
   * translated template rather than being hardcoded per component.
   */
  const barSummary = (label: string, amount: string, orders: string): string =>
    t(keys.dashboard.barSummary, { label, amount, orders });
  /**
   * Joins two already-localized fragments (money · count, delta · basis).
   *
   * Each fragment is wrapped in a directional ISOLATE, and that is load-bearing.
   * Without it "هامش 100‎%‎ · 5 طلبات" renders as "هامش 5 · 100‎%‎ طلبات" — the two
   * numbers visually SWAP, so the seller reads the margin as the order count and
   * vice versa. Cause: percent formatting embeds U+200E around the "%", opening
   * an LTR run that swallows the neutral "·" and the digits after it, merging
   * both figures into one left-to-right run inside an RTL line. Isolating each
   * fragment stops either from absorbing the other. `<bdi>` would do the same
   * job, but these fragments are interpolated into a translated string, so the
   * isolate has to travel as characters rather than markup.
   */
  const dotPair = (first: string, second: string): string =>
    t(keys.dashboard.dotPair, {
      first: isolate(first),
      second: isolate(second),
    });

  const { lossBreakdown, paymentHealth, discountImpact } = analytics;

  /**
   * The three headline measures, at equal weight. Every value, delta and
   * sparkline comes straight from the SQL aggregate — nothing is recomputed
   * here. Built below the gates so `analytics` is known non-null.
   *
   * Profit carries the "estimated" chip whenever some sold items have no
   * recorded cost: those items contributed zero cost, so the figure is an upper
   * bound and must not be presented as fact.
   */
  const headlineTiles: DashboardHeadlineTile[] = [
    {
      kind: "sales",
      label: t(keys.dashboard.headlineSales),
      value: moneyParts(analytics.sales.value),
      deltaPct: toDelta(analytics.sales.deltaPct),
      series: analytics.sales.series,
      meta: t(keys.dashboard.headlineSalesMeta, {
        count: analytics.orders.value,
      }),
    },
    {
      kind: "profit",
      label: t(keys.dashboard.headlineProfit),
      value: moneyParts(analytics.profit.value),
      deltaPct: toDelta(analytics.profit.deltaPct),
      series: analytics.profit.series,
      // Profit is money, so it carries its order count like Sales does — it is
      // earned on exactly the same delivered orders, so the count is a fact
      // rather than a second definition. Margin is only meaningful against
      // non-zero sales, so with no sales the tile shows the count alone.
      meta:
        analytics.sales.value > 0
          ? dotPair(
              t(keys.dashboard.headlineProfitMargin, {
                margin: percent(analytics.profit.value / analytics.sales.value),
              }),
              orderCount(analytics.orders.value),
            )
          : orderCount(analytics.orders.value),
      estimatedLabel:
        analytics.ordersMissingCost > 0
          ? t(keys.dashboard.headlineProfitEstimated)
          : undefined,
      hint: profitHint,
      hintHref: analytics.ordersMissingCost > 0 ? productsListHref : undefined,
      hintCta:
        analytics.ordersMissingCost > 0
          ? t(keys.dashboard.headlineProfitCta)
          : undefined,
    },
    {
      kind: "loss",
      label: t(keys.dashboard.headlineLoss),
      value: moneyParts(lossBreakdown.lostAmount),
      // The loss block is a single total for the window with no per-bucket
      // series and no previous-period baseline, so the tile shows neither a
      // delta nor a sparkline rather than inventing one.
      series: [],
      meta: t(keys.dashboard.headlineLossMeta, {
        count: lossBreakdown.lostOrders,
      }),
    },
  ];

  /**
   * Order fate: collected · pending · lost, over the SAME window, so the three
   * segments are directly comparable and sum to the whole.
   *
   * The three buckets are disjoint BY CONSTRUCTION, not by arithmetic here:
   * `lossBreakdown` is scoped server-side to TRUE_LOSS_STATUSES (draft,
   * cancelled, declined) and `pendingAmount` to PENDING_STATUSES, which is
   * derived as (enum − sales − true losses). So collected + pending + lost is
   * exhaustive and non-overlapping without this page subtracting anything.
   *
   * It used to compute `lostAmount − pendingAmount`, because `lossBreakdown`
   * was then the complement of sales and swept the pending family in with the
   * real losses. That also meant the headline Loss tile and this bar quoted two
   * different figures for the same word. Both now read the same field.
   */

  const fateSegments: DashboardFateSegment[] = [
    {
      id: "collected",
      label: t(keys.dashboard.fateCollected),
      amount: analytics.sales.value,
      orders: analytics.orders.value,
      ordersLabel: orderCount(analytics.orders.value),
      color: "var(--chart-sales)",
      ink: "var(--chart-sales-ink)",
    },
    {
      // Gold, not a third series colour: it is the only token that holds the
      // same value in both themes, and blue / gold / red separate by lightness
      // as well as hue, which is what keeps the triad readable under CVD.
      id: "pending",
      label: t(keys.dashboard.fatePending),
      amount: analytics.pendingAmount,
      orders: analytics.pendingOrders,
      ordersLabel: orderCount(analytics.pendingOrders),
      color: "var(--gold-500)",
      ink: "var(--chart-pending-ink)",
    },
    {
      id: "lost",
      label: t(keys.dashboard.fateLost),
      ink: "var(--chart-loss-ink)",
      amount: lossBreakdown.lostAmount,
      orders: lossBreakdown.lostOrders,
      ordersLabel: orderCount(lossBreakdown.lostOrders),
      color: "var(--error-500)",
    },
  ];
  // `sales + lostAmount` IS the window (lostAmount is the complement of sales),
  // and it equals collected + pending + lost by construction above.
  const fateTotal =
    analytics.sales.value + analytics.pendingAmount + lossBreakdown.lostAmount;
  const collectionRate = fateTotal > 0 ? analytics.sales.value / fateTotal : 0;

  /**
   * Ranked governorates. Localized here (not server-side) so the response stays
   * locale-agnostic; `nameEn` is optional in the seed data, so Arabic is the
   * fallback in both directions rather than rendering an empty label.
   */
  const governorateRows: DashboardGovernorateBar[] = analytics.topGovernorates
    .slice()
    .sort((a, b) => b.sales - a.sales)
    .map((row) => ({
      id: row.governorateId,
      name: locale === "ar" ? row.nameAr : row.nameEn?.trim() || row.nameAr,
      sales: row.sales,
      orders: row.orders,
    }));

  /**
   * COD vs online. The two rails are a categorical split of one measure, so
   * they reuse the two validated chart slots rather than introducing a third
   * palette. Each names its own ink token: white on the online/profit fill
   * measured 3.20:1, the worst on the page, because "dark enough for white"
   * was assumed rather than measured.
   */
  const paymentMixSlices: DashboardPaymentMixSlice[] = [
    {
      id: "cod",
      label: t(keys.dashboard.mixCod),
      sales: analytics.paymentMix.cod.sales,
      orders: analytics.paymentMix.cod.orders,
      ordersLabel: orderCount(analytics.paymentMix.cod.orders),
      color: CHART_SALES_COLOR,
      ink: "var(--chart-sales-ink)",
    },
    {
      id: "online",
      label: t(keys.dashboard.mixOnline),
      sales: analytics.paymentMix.online.sales,
      orders: analytics.paymentMix.online.orders,
      ordersLabel: orderCount(analytics.paymentMix.online.orders),
      color: CHART_PROFIT_COLOR,
      ink: "var(--chart-profit-ink)",
    },
  ];

  // Completion rate over the same window: delivered orders as a share of every
  // order that reached a terminal outcome (delivered + lost).
  const terminalOrders =
    analytics.orders.value + analytics.pendingOrders + lossBreakdown.lostOrders;
  const completionRate =
    terminalOrders > 0 ? analytics.orders.value / terminalOrders : 0;

  /**
   * Abandoned checkouts, read off the loss breakdown the page already has —
   * `abandoned` is the backend's synthetic reason code for drafts that never
   * became an order. No extra field and no second request: the same number
   * qualifies the completion rate and names the recoverable money below.
   */
  const abandonedOrders =
    lossBreakdown.reasons.find((reason) => reason.code === "abandoned")
      ?.orders ?? 0;

  /**
   * Period-over-period change as a sub-line. `deltaPct` is `null` when the
   * previous period was 0 — there is no baseline, so the tile shows no
   * sub-line at all rather than claiming "0%" or "+∞". The API reports the
   * delta in percent units (12.5 = 12.5%), so it is scaled back to a fraction
   * for `Intl.NumberFormat`'s percent style, which localizes the sign-free
   * magnitude; direction is carried by the ▲/▼ in the message itself.
   */
  const deltaHint = (deltaPct: number | null): string | undefined => {
    if (deltaPct === null) return undefined;
    const magnitude = percent(Math.abs(deltaPct) / 100);
    return deltaPct >= 0
      ? t(keys.dashboard.statsDeltaUp, { delta: magnitude })
      : t(keys.dashboard.statsDeltaDown, { delta: magnitude });
  };

  const avgOrderValueParts = moneyParts(analytics.avgOrderValue.value);

  const storeStats: DashboardStoreStat[] = [
    {
      id: "delivered",
      label: t(keys.dashboard.statsDelivered),
      value: analytics.orders.value.toLocaleString(intlLocale(locale)),
      hint: deltaHint(analytics.orders.deltaPct),
    },
    {
      // AOV is money *per order*, so repeating the order count as a companion
      // figure would be noise — but the DENOMINATOR is not noise: an average
      // over 3 orders and one over 300 are different claims. The hint therefore
      // names the basis rather than a count of its own.
      id: "aov",
      label: avgOrderValueLabel,
      // The ONLY money stat in this grid, so the only one that carries a
      // currency unit — the delivered/customers counts and the completion rate
      // beside it are bare figures.
      value: avgOrderValueParts.amount,
      currency: {
        unit: avgOrderValueParts.unit,
        unitFirst: avgOrderValueParts.unitFirst,
      },
      hint: (() => {
        const basis = t(keys.dashboard.statsAovBasis, {
          count: analytics.orders.value,
        });
        const delta = deltaHint(analytics.avgOrderValue.deltaPct);
        return delta ? dotPair(delta, basis) : basis;
      })(),
    },
    {
      // New vs returning comes from the API as a pair that sums to
      // `customers.value`, so the share is computed against that total rather
      // than against a second, independently-counted denominator.
      id: "customers",
      label: t(keys.dashboard.statsCustomers),
      value: analytics.customers.value.toLocaleString(intlLocale(locale)),
      hint:
        analytics.customers.value > 0
          ? t(keys.dashboard.statsCustomersHint, {
              new: analytics.customersBreakdown.new.toLocaleString(intlLocale(locale)),
              returning:
                analytics.customersBreakdown.returning.toLocaleString(intlLocale(locale)),
              share: percent(
                analytics.customersBreakdown.returning /
                  analytics.customers.value,
              ),
            })
          : undefined,
    },
    {
      id: "completion",
      label: t(keys.dashboard.statsCompletion),
      value: percent(completionRate),
      hint: t(keys.dashboard.statsCompletionHint, {
        abandoned: abandonedOrders.toLocaleString(intlLocale(locale)),
        total: terminalOrders.toLocaleString(intlLocale(locale)),
      }),
    },
    {
      // Catalogue health is point-in-time, not range-scoped — it does not move
      // when the range selector does, by design.
      id: "catalog",
      label: t(keys.dashboard.statsActiveProducts),
      value: t(keys.dashboard.statsActiveProductsValue, {
        active: analytics.catalog.activeProducts.toLocaleString(intlLocale(locale)),
        total: analytics.catalog.totalProducts.toLocaleString(intlLocale(locale)),
      }),
      hint: t(keys.dashboard.statsCatalogHint, {
        outOfStock: analytics.catalog.outOfStock.toLocaleString(intlLocale(locale)),
        hidden: analytics.catalog.hidden.toLocaleString(intlLocale(locale)),
      }),
    },
    {
      // `reviews === null` means the store has NO reviews. "0 of 5" would be a
      // claim of a terrible rating rather than an absence of evidence, so the
      // tile says so in words instead.
      //
      // `reviews.pendingReply` is deliberately not rendered: it is hardcoded to
      // 0 server-side because no reply feature exists, so showing it would
      // assert "you have replied to everything", which is not a fact.
      id: "rating",
      label: t(keys.dashboard.statsRating),
      value: analytics.reviews
        ? t(keys.dashboard.statsRatingValue, {
            rating: analytics.reviews.averageRating.toLocaleString(intlLocale(locale), {
              minimumFractionDigits: 1,
              maximumFractionDigits: 1,
            }),
          })
        : t(keys.dashboard.statsRatingEmpty),
      hint: analytics.reviews
        ? t(keys.dashboard.statsRatingCount, {
            count: analytics.reviews.total,
          })
        : t(keys.dashboard.statsRatingEmptyHint),
    },
  ];

  /**
   * Backend loss reasons are enum codes. `abandoned` and `unspecified` are the
   * two synthetic ones; anything else is a cancel/decline code, and rather than
   * duplicating the orders module's full reason catalogue here the panel falls
   * back to naming the terminal status, which is always meaningful.
   */
  /**
   * Name the REASON an order was lost, not the status it ended in.
   *
   * Every `CancelReasonCode` gets its own label. The previous version handled
   * only `abandoned` and `unspecified` and let everything else fall through to
   * "cancelled" — so an order lost to `out_of_stock` was reported as "ملغي",
   * telling the seller *that* it was cancelled while hiding the one fact they
   * could act on. The fallthrough also meant every future reason code would
   * silently collapse into the same wrong label.
   *
   * `status` is only consulted for `unspecified`, where there genuinely is no
   * recorded reason and the status is the most specific thing left to say.
   */
  const lossReasonLabel = (code: string, status: string): string => {
    const byCode: Record<string, string> = {
      abandoned: keys.dashboard.lossReasonAbandoned,
      out_of_stock: keys.orders.cancelReasonOutOfStock,
      customer_requested: keys.orders.cancelReasonCustomerRequested,
      cannot_deliver: keys.orders.cancelReasonCannotDeliver,
      other: keys.orders.cancelReasonOther,
    };
    const known = byCode[code];
    if (known) return t(known);

    if (code === "unspecified") {
      return status === "declined"
        ? t(keys.dashboard.lossReasonDeclined)
        : status === "cancelled"
          ? t(keys.dashboard.lossReasonCancelled)
          : t(keys.dashboard.lossReasonUnspecified);
    }
    // An unrecognised code is a real gap, not a cancelled order — say the least
    // wrong thing rather than asserting a reason we do not have.
    return t(keys.dashboard.lossReasonUnspecified);
  };

  const chartBucketLabels = localizeBucketLabels(
    analytics.bucketLabels,
    analytics.range,
    locale,
  );

  return (
    <>
      <PageTransition className="w-full">
        <div
          className={[
            "flex w-full flex-col gap-[18px]",
            isMobile ? "px-4 pb-24 pt-4" : "px-7 py-6",
          ].join(" ")}
        >
          {/* Header: what this page reports on (store + window), then the
              window selector. No greeting — see `pageTitle` above. */}
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[11.5px] font-semibold uppercase tracking-[0.08em] text-[color:var(--text-muted)]">
                {t(keys.dashboard.eyebrow)}
              </p>
              <h1 className="mt-[3px] text-[25px] font-bold leading-tight tracking-[-0.01em] text-[color:var(--text-primary)]">
                <bdi>{pageTitle}</bdi>
              </h1>
            </div>
            <div className="flex min-w-0 flex-wrap items-center gap-2.5 lg:shrink-0">
              <div className="inline-flex items-center gap-0.5 rounded-[11px] border border-[color:var(--line2)] bg-[color:var(--content)] p-[3px]">
                {RANGE_OPTIONS.map((option) => {
                  const active = range === option;
                  return (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setRange(option)}
                      aria-pressed={active}
                      className={[
                        "rounded-[8px] px-3 py-1.5 text-[12.5px] transition-colors",
                        active
                          ? "bg-[color:var(--card)] font-bold text-[color:var(--text-primary)] shadow-[0_1px_2px_rgba(16,16,15,0.06)]"
                          : "font-medium text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]",
                      ].join(" ")}
                    >
                      <bdi>{t(RANGE_PILL_KEYS[option])}</bdi>
                    </button>
                  );
                })}
              </div>
              <Link
                to={productsNewHref}
                className="inline-flex items-center gap-2 rounded-[11px] bg-[color:var(--accent-500)] px-4 py-[9px] text-[13.5px] font-semibold text-[color:var(--on-accent)]"
              >
                <svg
                  width="17"
                  height="17"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M12 5v14M5 12h14" />
                </svg>
                {addProductLabel}
              </Link>
            </div>
          </div>

          {/* 1. Three headline tiles, equal weight: sales · profit · loss. */}
          <div className="flex flex-col gap-2">
            <DashboardHeadlineTiles tiles={headlineTiles} />
            {/* Revenue is recognised on DELIVERY only — the backend counts just
                `delivered` orders. Every tile, the chart and the top-products
                list share that scope, so it is stated once here rather than
                repeated as a hint on every tile. Without it "Sales" reads as
                "everything the buyers ordered", which it is not. */}
            <p className="px-1 text-[11.5px] leading-[1.45] text-[color:var(--text-muted)]">
              {t(keys.dashboard.kpiRealisedScope)}
            </p>
          </div>

          {/* 2. Sales vs Profit over the range — both money, ONE shared axis. */}
          <DashboardRevenueCard
            title={t(keys.dashboard.chartTitle)}
            subtitle={t(keys.dashboard.chartSubtitle, {
              range: rangeLongLabel,
            })}
            series={[
              {
                label: t(keys.dashboard.chartLegendSales),
                values: analytics.sales.series,
                color: CHART_SALES_COLOR,
              },
              {
                label: t(keys.dashboard.chartLegendProfit),
                values: analytics.profit.series,
                color: CHART_PROFIT_COLOR,
              },
            ]}
            bucketLabels={chartBucketLabels}
            direction={getLocaleDirection(locale as Locale)}
            /* Per-bucket delivered order counts, straight from the same SQL
               aggregate as the plotted money — the chart's two series are both
               money, so the tooltip would otherwise report a sum with no idea
               how many orders produced it. Not derived from the money. */
            orders={analytics.orders.series}
            ordersLabel={orderCount}
            formatValue={money}
            emptyLabel={t(keys.dashboard.chartEmpty)}
          />

          {/* 3. Order fate — collected vs lost, as a composition bar. */}
          <DashboardOrderFateCard
            title={t(keys.dashboard.fateTitle)}
            subtitle={t(keys.dashboard.fateSubtitle, {
              total: money(fateTotal),
            })}
            segments={fateSegments}
            /* The closing line names the biggest RECOVERABLE sum — carts
               abandoned at the payment step — because that is the one bucket
               the seller can still act on. With no abandoned carts there is
               nothing to recover, so it falls back to how much of the window's
               money was actually collected rather than printing "0 carts". */
            takeaway={
              abandonedOrders > 0
                ? t(keys.dashboard.fateAbandonedNote, {
                    count: abandonedOrders,
                  })
                : t(keys.dashboard.fateTakeaway, {
                    rate: percent(collectionRate),
                  })
            }
            emptyTitle={t(keys.dashboard.fateEmptyTitle)}
            emptyBody={t(keys.dashboard.fateEmptyBody)}
            formatValue={money}
            barSummary={barSummary}
          />

          {/* 4. Store statistics — tiles plus the ranked governorates block. */}
          <DashboardStoreStatsCard
            title={t(keys.dashboard.statsTitle)}
            subtitle={t(keys.dashboard.statsSubtitle)}
            stats={storeStats}
            governorates={{
              title: t(keys.dashboard.statsGovTitle),
              subtitle: t(keys.dashboard.statsGovSubtitle),
              rows: governorateRows,
              emptyLabel: t(keys.dashboard.statsGovEmpty),
              ordersLabel: orderCount,
              formatValue: money,
              barSummary,
            }}
            /* How the collected money ARRIVED — same question as the
               governorates block ("where did it come from"), so it sits beside
               it in this card rather than in a card of its own. Distinct from
               the payment HEALTH panel below, which counts gateway attempts. */
            paymentMix={{
              title: t(keys.dashboard.mixTitle),
              subtitle: t(keys.dashboard.mixSubtitle),
              slices: paymentMixSlices,
              emptyLabel: t(keys.dashboard.mixEmptyBody),
              formatValue: money,
              barSummary,
            }}
          />

          {/* 5. Three signal panels. Each renders a real empty state — which is
              the common case in production today — rather than a row of zeros. */}
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            <DashboardPaymentHealthPanel
              health={paymentHealth}
              formatValue={money}
              formatPercent={percent}
              formatCount={countValue}
            />
            <DashboardDiscountImpactPanel
              impact={discountImpact}
              formatValue={money}
              formatValueParts={moneyParts}
              formatPercent={percent}
            />
            <DashboardLossBreakdownPanel
              loss={lossBreakdown}
              formatValue={money}
              formatValueParts={moneyParts}
              reasonLabel={lossReasonLabel}
            />
          </div>

          {/* 6. Best sellers — aggregate rows (units/revenue/profit) from the
              analytics endpoint, so this is NOT DashboardProductListCard: these
              rows carry no status, stock or thumbnail to render. The profit
              column is part of that existing list pattern. */}
          <DashboardTopProductsCard
            title={t(keys.dashboard.topProductsTitle)}
            subtitle={t(keys.dashboard.topProductsSubtitle)}
            emptyLabel={t(keys.dashboard.topProductsEmpty)}
            products={analytics.topProducts}
            locale={locale as Locale}
            productHref={productEditHref}
          />

          {/* The approved design ends here. Recent orders and low stock are
              deliberately NOT on the dashboard: both have dedicated pages
              (Orders, Products) and repeating them turned this screen into a
              second navigation surface instead of a report. */}
        </div>
      </PageTransition>
    </>
  );
}
